import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Add,
  Check,
  ChevronLeft,
  ChevronRight,
  DeleteOutline,
  DragIndicator,
  Inventory2Outlined,
  PersonAddOutlined,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import NotificationSnackbar from './ui/NotificationSnackbar';
import AddProductDialog from './products/AddProductDialog';
import AddLeadDialog from './leads/AddLeadDialog';
import { getSettings } from '../services/settingsService';
import { displayCurrency } from '../utils/currencyUtils';
import { fetchAllProducts, createProduct, updateProduct } from '../services/productServices';
import { createQuotation } from '../services/quotationService';
import { fetchLeads } from '../services/leadService';
import { listDocumentProfiles, resolveDocumentProfile } from '../services/documentProfileService';
import { calculateQuotationTotals } from '../utils/quotationCalculator';
import { applyPackageDefinitions } from '../utils/quotationPackages';
import QuotationPackageManager from './quotation/QuotationPackageManager';
import { formatDate, toInputDateValue } from '../utils/dateFormatter';
import { useSettings } from '../context/SettingsContext';
import { CrmCheckbox, CrmDateInput, CrmInput, CrmSearchInput, CrmSelect, CrmTextarea, CrmTimeInput } from './ui/CrmFormControls';
import { CrmButton, CrmDrawer } from './ui';
import api from '../services/api';
import '../assets/styles/QuotationBuilder.scss';

const leadName = (lead) => {
  if (!lead) return '';
  return String(lead.full_name || lead.contact_name || lead.name || `${lead.first_name || ''} ${lead.last_name || ''}` || '').trim() || '';
};

const companyName = (lead) => {
  const company = lead?.company;
  const companyFallback = typeof company === 'string' || typeof company === 'number'
    ? String(company)
    : (company?.name || company?.company_name || company?.legal_name || '');
  return lead?.company_name || companyFallback || lead?.organization_name || '';
};
const contactAddress = (lead) => [lead?.address, lead?.address_line1, lead?.address_line2, lead?.city, lead?.state, lead?.pincode]
  .filter(Boolean).join(', ') || '';
const productName = (product) => product?.name || product?.product_name || `Product ${product?.id || ''}`;
const productCategory = (product) => product?.category_name || product?.category?.name || 'Uncategorised';
const toDateInput = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : toInputDateValue(parsed);
};

const toTimeInput = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{2}:\d{2})/);
  if (match) return match[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
};

const eventChronologyError = (meta = {}) => {
  const startDate = toDateInput(meta.event_start_date);
  const endDate = toDateInput(meta.event_end_date);
  const startTime = toTimeInput(meta.event_start_time);
  const endTime = toTimeInput(meta.event_end_time);
  if (startDate && endDate && endDate < startDate) return 'End date cannot be before the start date.';
  if (startDate && endDate && startDate === endDate && startTime && endTime && endTime < startTime) {
    return 'End time cannot be before the start time on the same day.';
  }
  return '';
};

const relationName = (rows, id, fallback = 'Global') => {
  if (!id) return fallback;
  const row = (rows || []).find((item) => Number(item.id) === Number(id));
  return row?.name || row?.legal_name || row?.trade_name || row?.code || fallback;
};

const profileScopeLabel = (profile, catalog = {}) => {
  if (!profile) return 'Default CRM scope';
  if (profile.storefront_id) return `Storefront · ${relationName(catalog.storefronts, profile.storefront_id)}`;
  if (profile.business_unit_id) return `Business unit · ${relationName(catalog.business_units, profile.business_unit_id)}`;
  if (profile.brand_id) return `Brand · ${relationName(catalog.brands, profile.brand_id)}`;
  if (profile.legal_entity_id) return `Legal entity · ${relationName(catalog.legal_entities, profile.legal_entity_id)}`;
  return 'Global';
};

const money = (value, currency) => `${currency} ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function QuotationPreview({ profile, profileLoading, profileError, context, selectedLead, quotationDate, validUntil, items, cateringMeta, pax, quotationMode, gstPricingMode, overallDiscount, roundingAmount }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profileLoading) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.post('/quotations/draft-preview/html', {
          profileId: profile?.id || null,
          context,
          quotation: {
            first_name: selectedLead?.first_name || null,
            last_name: selectedLead?.last_name || null,
            contact_name: leadName(selectedLead),
            company_name: companyName(selectedLead),
            billing_address: selectedLead?.billing_address || selectedLead?.address || '',
            billing_city: selectedLead?.billing_city || selectedLead?.city || '',
            billing_state: selectedLead?.billing_state || selectedLead?.state || '',
            billing_pincode: selectedLead?.billing_pincode || selectedLead?.pincode || '',
            gst_number: selectedLead?.gst_number || selectedLead?.gstin || '',
            quotation_date: quotationDate,
            valid_until: validUntil,
            quotation_mode: quotationMode,
            pax,
            ...cateringMeta,
            quotation_discount_value: overallDiscount,
            rounding_amount: roundingAmount,
          },
          items,
          gstPricingMode,
        }, { responseType: 'text' });
        if (active) { setHtml(response.data); setError(''); }
      } catch (previewError) {
        if (active) { setHtml(''); setError(previewError?.response?.data?.error || 'Unable to render quotation preview.'); }
      } finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [profile, profileLoading, context, selectedLead, quotationDate, validUntil, items, cateringMeta, pax, quotationMode, gstPricingMode, overallDiscount, roundingAmount]);

  return <aside className="jdh-quote-preview">
    <div className="jdh-quote-preview__toolbar"><div><strong>Live preview</strong><span>{profile?.name || 'Resolved quotation template'}</span></div></div>
    {profileError || error ? <div className="jdh-quote-preview__notice">{error || profileError}</div> : null}
    <div className="jdh-quote-preview__viewport">
      {loading && !html ? <div className="jdh-quote-preview__notice">Rendering quotation?</div> : null}
      {html ? <iframe className={`jdh-quote-preview__document is-${String(profile?.page_size || 'A4').toLowerCase()} ${String(profile?.orientation || '').toLowerCase() === 'landscape' ? 'is-landscape' : ''}`} title="Quotation document preview" srcDoc={html} sandbox="allow-same-origin" /> : null}
    </div>
  </aside>;
}

function CreateQuotation() {
  const navigate = useNavigate();
  const { leadId: routeLeadId } = useParams();
  const { settings: globalSettings, commercialCatalog, reloadOperationalContext } = useSettings();
  const catalog = commercialCatalog || {};
  const crmCommercialContext = useMemo(() => {
    const businessUnit = (catalog.business_units || []).find((row) => String(row.code || '').toUpperCase() === 'JDH_CATERING');
    const brand = (catalog.brands || []).find((row) => Number(row.id) === Number(businessUnit?.brand_id || 0))
      || (catalog.brands || []).find((row) => String(row.code || '').toUpperCase() === 'JDH_CATERERS');
    const legalEntity = (catalog.legal_entities || []).find((row) => Number(row.id) === Number(businessUnit?.legal_entity_id || 0))
      || (catalog.legal_entities || []).find((row) => String(row.code || '').toUpperCase() === 'JDH_DEFAULT');
    return {
      legal_entity_id: legalEntity?.id || null,
      brand_id: brand?.id || null,
      business_unit_id: businessUnit?.id || null,
      storefront_id: null,
    };
  }, [catalog.business_units, catalog.brands, catalog.legal_entities]);
  const dateFormat = globalSettings?.date_format || 'DD/MM/YYYY';

  const [step, setStep] = useState(0);
  const [gstPricingMode, setGstPricingMode] = useState('INCLUSIVE');
  const [currency, setCurrency] = useState('₹');
  const [quotationMode, setQuotationMode] = useState('GENERAL');
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadQuery, setLeadQuery] = useState('');
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [prefillLeadName, setPrefillLeadName] = useState('');
  const [quotationDate, setQuotationDate] = useState(toInputDateValue(new Date()));
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [productPrefilledLeadId, setProductPrefilledLeadId] = useState(null);
  const [pax, setPax] = useState(null);
  const [cateringMeta, setCateringMeta] = useState({
    event_name: '', event_start_date: '', event_start_time: '11:00', event_end_date: '', event_end_time: '23:00', event_location: '',
  });
  const [overallDiscount, setOverallDiscount] = useState(0);
  const [roundingAmount, setRoundingAmount] = useState(null);
  const [roundingManual, setRoundingManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState({ open: false, message: '', severity: 'success' });
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productQuery, setProductQuery] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productSort, setProductSort] = useState('name-asc');
  const [pickedProducts, setPickedProducts] = useState([]);
  const [pickerPackageKey, setPickerPackageKey] = useState('');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [resolvedProfile, setResolvedProfile] = useState(null);
  const [templateProfiles, setTemplateProfiles] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('auto');
  const [packages, setPackages] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  const showNotification = useCallback((message, severity = 'success') => setNotif({ open: true, message, severity }), []);

  const steps = useMemo(() => quotationMode === 'CATERING'
    ? ['Details', 'Customer', 'Event & PAX', 'Products', 'Review']
    : ['Details', 'Customer', 'Products', 'Review'], [quotationMode]);

  const productStepIndex = quotationMode === 'CATERING' ? 3 : 2;
  const reviewStepIndex = steps.length - 1;

  useEffect(() => {
    Promise.all([getSettings(), fetchAllProducts(), fetchLeads(), listDocumentProfiles('QUOTATION').catch(() => [])])
      .then(([settings, productResponse, leadResponse, profileResponse]) => {
        setTemplateProfiles(Array.isArray(profileResponse) ? profileResponse : profileResponse?.items || []);
        const mode = String(settings?.business_type || 'GENERAL').toUpperCase();
        setQuotationMode(mode);
        setGstPricingMode(String(settings?.gst_pricing_mode || 'INCLUSIVE').toUpperCase());
        setCurrency(displayCurrency(settings?.currency_code || 'INR'));

        const productList = Array.isArray(productResponse) ? productResponse : productResponse?.data || productResponse?.products || [];
        setProducts(Array.isArray(productList) ? productList : []);

        const leadList = Array.isArray(leadResponse) ? leadResponse : leadResponse?.leads || leadResponse?.data || [];
        setLeads(Array.isArray(leadList) ? leadList : []);
        if (routeLeadId) {
          const match = (leadList || []).find((lead) => String(lead.id) === String(routeLeadId));
          if (match) {
            setSelectedLead(match);
            setLeadId(match.id);
          }
        }
      })
      .catch((error) => showNotification(error?.response?.data?.error || 'Failed to load quotation builder data.', 'error'));
  }, [routeLeadId, showNotification]);

  useEffect(() => {
    if (!commercialCatalog) reloadOperationalContext?.();
  }, [commercialCatalog, reloadOperationalContext]);

  useEffect(() => {
    if (!selectedLead || quotationMode !== 'CATERING') return;
    setPax(selectedLead?.pax ? Number(selectedLead.pax) : null);
    setCateringMeta({
      event_name: selectedLead?.event_name || selectedLead?.event_type || '',
      event_start_date: toDateInput(selectedLead?.event_start_date || selectedLead?.event_date),
      event_start_time: toTimeInput(selectedLead?.event_start_time || selectedLead?.event_time) || '11:00',
      event_end_date: toDateInput(selectedLead?.event_end_date),
      event_end_time: toTimeInput(selectedLead?.event_end_time) || '23:00',
      event_location: selectedLead?.event_location || '',
    });
  }, [selectedLead, quotationMode]);

  useEffect(() => {
    if (quotationMode === 'CATERING') return;
    setPax(null);
    setCateringMeta({ event_name: '', event_start_date: '', event_start_time: '11:00', event_end_date: '', event_end_time: '23:00', event_location: '' });
  }, [quotationMode]);

  const makeItem = useCallback((product, quantity = 1) => ({
    product,
    quantity,
    selling_price: Number(product?.selling_price || product?.price || 0),
    selling_price_unit: product?.selling_price_unit || 'unit',
    cost_price: Number(product?.cost_price || 0),
    discount: 0,
    gst_rate: Number(product?.gst_rate || 0),
    variant_id: product?.variantId || null,
    cost_price_unit: product?.cost_price_unit || 'unit',
    cost_price_qty: Number(product?.cost_price_qty || 1),
    cost_pricing_mode: product?.cost_pricing_mode || 'absolute',
    cost_discount_percent: Number(product?.cost_discount_percent || 0),
    package_key: null,
  }), []);

  useEffect(() => {
    if (!routeLeadId || !selectedLead || !products.length) return;
    if (String(productPrefilledLeadId) === String(selectedLead.id) || items.length) return;
    setProductPrefilledLeadId(selectedLead.id);

    const leadProductId = Number(selectedLead?.product_id || 0);
    const leadProductName = String(selectedLead?.product_name || '').trim().toLowerCase();
    const product = products.find((row) => leadProductId > 0 && Number(row.id) === leadProductId)
      || products.find((row) => leadProductName && String(row.name || '').trim().toLowerCase() === leadProductName);
    if (!product) return;

    setItems([makeItem(product, 1)]);
  }, [routeLeadId, selectedLead, products, productPrefilledLeadId, items.length, quotationMode, makeItem]);

  useEffect(() => {
    let active = true;
    // A CRM-created quotation belongs to the CRM/JDH commercial context. The
    // selected contact may have originated from a storefront, but that customer
    // history must never decide this quotation's fulfilment workflow.
    const context = {
      legal_entity_id: crmCommercialContext.legal_entity_id || undefined,
      brand_id: crmCommercialContext.brand_id || undefined,
      business_unit_id: crmCommercialContext.business_unit_id || undefined,
      storefront_id: undefined,
    };
    setProfileLoading(true);
    setProfileError('');
    setResolvedProfile(null);
    if (selectedTemplateId !== 'auto') {
      const selected = templateProfiles.find((profile) => String(profile.id) === String(selectedTemplateId));
      setResolvedProfile(selected || null);
      setProfileLoading(false);
      return () => { active = false; };
    }
    resolveDocumentProfile('QUOTATION', context)
      .then((response) => { if (active) setResolvedProfile(response?.profile || response || null); })
      .catch(() => { if (active) { setResolvedProfile(null); setProfileError('Unable to resolve the quotation template.'); } })
      .finally(() => { if (active) setProfileLoading(false); });
    return () => { active = false; };
  }, [crmCommercialContext.legal_entity_id, crmCommercialContext.brand_id, crmCommercialContext.business_unit_id, selectedTemplateId, templateProfiles]);

  const updateItem = (index, changes) => setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item));
  const removeItem = (index) => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  const reorderItems = (from, to) => {
    if (from == null || to == null || from === to) return;
    setItems((current) => {
      if (from < 0 || to < 0 || from >= current.length || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const pricedItems = useMemo(() => applyPackageDefinitions(items, packages), [items, packages]);

  const packageTotals = useMemo(() => calculateQuotationTotals({
    items: pricedItems,
    overallDiscount,
    quotationMode,
    gstPricingMode
  }), [pricedItems, overallDiscount, quotationMode, gstPricingMode]);

  const totals = useMemo(() => ({
    ...packageTotals,
    packageTotal: Number(packageTotals.grandTotal || 0),
    packageSubtotal: Number(packageTotals.discountedSubtotal || 0),
  }), [packageTotals]);

  useEffect(() => {
    if (roundingManual) return;
    const grand = Number(totals.grandTotal || 0);
    setRoundingAmount(Number((Math.round(grand) - grand).toFixed(2)));
  }, [totals.grandTotal, roundingManual]);

  const categories = useMemo(() => [...new Set(products.map(productCategory).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [products]);
  const pickerProducts = useMemo(() => {
    const query = productQuery.trim().toLowerCase();
    const filtered = products.filter((product) => {
      if (productCategoryFilter !== 'all' && productCategory(product) !== productCategoryFilter) return false;
      if (!query) return true;
      return [productName(product), product.sku, productCategory(product)].some((value) => String(value || '').toLowerCase().includes(query));
    });
    return filtered.sort((a, b) => {
      if (productSort === 'name-desc') return productName(b).localeCompare(productName(a));
      if (productSort === 'price-asc') return Number(a.selling_price || a.price || 0) - Number(b.selling_price || b.price || 0);
      if (productSort === 'price-desc') return Number(b.selling_price || b.price || 0) - Number(a.selling_price || a.price || 0);
      return productName(a).localeCompare(productName(b));
    });
  }, [products, productQuery, productCategoryFilter, productSort]);

  const visibleLeads = useMemo(() => {
    const query = leadQuery.trim().toLowerCase();
    const filtered = (query
      ? leads.filter((lead) => [leadName(lead), companyName(lead), lead.email, lead.phone_number]
        .some((value) => String(value || '').toLowerCase().includes(query)))
      : leads
    ).slice(0, query ? 12 : 8);
    if (!selectedLead) return filtered;
    const withoutSelected = filtered.filter((lead) => String(lead.id) !== String(selectedLead.id));
    return [selectedLead, ...withoutSelected].slice(0, query ? 12 : 8);
  }, [leads, leadQuery, selectedLead]);

  const addPickedProducts = () => {
    const targetPackageKey = quotationMode === 'CATERING' ? (pickerPackageKey || null) : null;
    const existing = new Set(items.map((item) => `${String(item.product?.id)}:${String(item.package_key || '')}`));
    const additions = products
      .filter((product) => pickedProducts.includes(String(product.id)) && !existing.has(`${String(product.id)}:${String(targetPackageKey || '')}`))
      .map((product) => ({ ...makeItem(product), package_key: targetPackageKey }));
    setItems((current) => [...current, ...additions]);
    setPickedProducts([]);
    setProductPickerOpen(false);
    if (!additions.length && pickedProducts.length) showNotification('Selected products are already in the quotation.', 'info');
  };

  const handleAddProduct = async (productData) => {
    try {
      const wasEditing = Boolean(editingProduct);
      const savedProduct = wasEditing
        ? await updateProduct(editingProduct.id, productData)
        : await createProduct(productData);
      const response = await fetchAllProducts();
      const list = Array.isArray(response) ? response : response?.data || response?.products || [];
      setProducts(Array.isArray(list) ? list : []);
      setOpenProductDialog(false);
      setEditingProduct(null);
      showNotification(wasEditing ? 'Product updated successfully.' : 'Product created successfully.');
      return savedProduct;
    } catch (error) {
      showNotification(error?.response?.data?.error || error?.message || 'Failed to save product.', 'error');
      throw error;
    }
  };

  const selectLead = (lead) => {
    setSelectedLead(lead);
    setLeadId(lead?.id || '');
    setLeadQuery('');
  };

  const validateItems = () => {
    if (!items.length) return 'Add at least one product.';
    const invalid = items.some((item) => !item.product?.id || Number(item.quantity) <= 0 || Number(item.selling_price) < 0);
    if (invalid) return 'Check product quantities and selling prices.';
    if (quotationMode === 'CATERING') {
      if (!pax || Number(pax) < 1) return 'PAX is required for catering quotations.';
    }
    return '';
  };

  const validateStep = (index) => {
    if (index === 0 && !quotationDate) return 'Quotation date is required.';
    if (index === 1 && !leadId) return 'Select a contact before continuing.';
    if (quotationMode === 'CATERING' && index === 2) {
      if (!cateringMeta.event_name?.trim()) return 'Event name is required before continuing.';
      if (!pax || Number(pax) < 1) return 'PAX is required before continuing.';
      if (!cateringMeta.event_location?.trim()) return 'Event venue is required before continuing.';
      if (!cateringMeta.event_start_date || !cateringMeta.event_start_time || !cateringMeta.event_end_date || !cateringMeta.event_end_time) return 'Start and end date/time are required before continuing.';
      const chronologyError = eventChronologyError(cateringMeta);
      if (chronologyError) return chronologyError;
    }
    if (index === productStepIndex) return validateItems();
    return '';
  };

  const next = () => {
    const error = validateStep(step);
    if (error) return showNotification(error, 'warning');
    setStep((current) => Math.min(reviewStepIndex, current + 1));
  };

  const goToStep = (index) => {
    if (index <= step) setStep(index);
  };

  const handleSubmit = async () => {
    if (!leadId) return showNotification('Contact is required.', 'warning');
    const itemError = validateItems();
    if (itemError) return showNotification(itemError, 'warning');
    if (quotationMode === 'CATERING') {
      const eventError = validateStep(2);
      if (eventError) return showNotification(eventError, 'warning');
    }

    const payload = {
      lead_id: leadId,
      quotation_date: quotationDate,
      valid_until: validUntil || null,
      notes: notes || null,
      quotation_discount_type: 'FLAT',
      quotation_discount_value: Number(overallDiscount || 0),
      quotation_discount_amount: Number(overallDiscount || 0),
      total_tax: Number(totals.totalTax || 0),
      grand_total: Number(totals.grandTotal || 0) + Number(roundingAmount || 0),
      rounding_amount: Number(roundingAmount || 0),
      document_profile_id: selectedTemplateId === 'auto' ? null : Number(selectedTemplateId),
      source: 'CRM',
      legal_entity_id: crmCommercialContext.legal_entity_id,
      brand_id: crmCommercialContext.brand_id,
      business_unit_id: crmCommercialContext.business_unit_id,
      storefront_id: null,
      pax_multiplier_enabled: false,
      items: pricedItems.map((item) => ({
        product_id: item.product.id,
        variant_id: item.variant_id || null,
        quantity: Number(item.quantity),
        unit_price: Number(item.selling_price),
        selling_price_unit: item.selling_price_unit || item.product?.selling_price_unit || 'unit',
        discount: Number(item.discount || 0),
        gst_rate: Number(item.gst_rate || 0),
        cost_price: Number(item.cost_price || 0),
        cost_price_unit: item.cost_price_unit || 'unit',
        cost_price_qty: Number(item.cost_price_qty || 1),
        cost_pricing_mode: item.cost_pricing_mode || 'absolute',
        cost_discount_percent: Number(item.cost_discount_percent || 0),
        package_key: item.package_key || null,
        package_name: item.package_name || null,
        package_pax: item.package_pax || null,
        package_pricing_mode: item.package_pricing_mode || 'ITEM_QUANTITY',
        package_sort_order: item.package_sort_order ?? null,
      })),
      ...(quotationMode === 'CATERING' ? {
        pax: Number(pax),
        event_name: cateringMeta.event_name || null,
        event_location: cateringMeta.event_location || null,
        event_start_date: cateringMeta.event_start_date || null,
        event_start_time: cateringMeta.event_start_time || null,
        event_end_date: cateringMeta.event_end_date || null,
        event_end_time: cateringMeta.event_end_time || null,
      } : {}),
    };

    setSaving(true);
    try {
      await createQuotation(payload);
      showNotification('Quotation created successfully.');
      navigate('/quotations');
    } catch (error) {
      showNotification(error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Failed to create quotation.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const lineTotal = (item) => {
    const pkg = packages.find((row) => String(row.key) === String(item.package_key || ''));
    const multiplier = String(pkg?.pricing_mode || 'ITEM_QUANTITY').toUpperCase() === 'PER_PAX' ? Math.max(Number(pkg?.pax || 1), 1) : 1;
    const base = Math.max((Number(item.quantity || 0) * multiplier * Number(item.selling_price || 0)) - (Number(item.discount || 0) * multiplier), 0);
    return gstPricingMode === 'EXCLUSIVE' ? base + (base * Number(item.gst_rate || 0) / 100) : base;
  };

  return (
    <main className="jdh-quotation-builder">
      <ol className="jdh-builder-steps">
        {steps.map((label, index) => (
          <li key={label} className={`${index === step ? 'active' : ''} ${index < step ? 'complete' : ''}`}>
            <button type="button" onClick={() => goToStep(index)} disabled={index > step}>
              <b>{label}</b><span>{index < step ? <Check /> : index + 1}</span>
            </button>
          </li>
        ))}
      </ol>

      <section className={`jdh-builder-body ${step === productStepIndex ? 'jdh-builder-body--wide' : ''}`}>
        <div className="jdh-builder-form">
          {step === 0 ? (
            <>
              <div className="jdh-builder-title"><div><span>Step 1</span><h1>Quotation details</h1><p>Set the document dates. The template is resolved automatically from the quotation's commercial context.</p></div></div>
              <div className="jdh-builder-fields two-col">
                <CrmDateInput label="Quotation Date" required value={quotationDate} dateFormat={dateFormat} onChange={setQuotationDate} />
                <CrmDateInput label="Valid Until" value={validUntil} dateFormat={dateFormat} onChange={setValidUntil} />
                <CrmSelect className="wide" label="Quotation Template" value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
                  <option value="auto">Automatic · resolve from commercial context</option>
                  {templateProfiles.filter((profile) => profile.enabled !== false && Number(profile.enabled ?? 1) !== 0).map((profile) => <option key={profile.id} value={profile.id}>{profile.name} · {profileScopeLabel(profile, catalog)}</option>)}
                </CrmSelect>
              </div>
              <section className="jdh-builder-template-card">
                <div><small>Resolved document template</small><strong>{profileLoading ? 'Resolving…' : resolvedProfile?.name || 'Default quotation template'}</strong><span>{profileScopeLabel(resolvedProfile, catalog)}</span></div>
                <div><small>Template style</small><strong>{resolvedProfile?.template_key || 'general'}</strong><span>Automatically follows Storefront → Business Unit → Brand → Legal Entity → Global.</span></div>
              </section>
              <CrmTextarea className="jdh-builder-notes" label="Internal / quotation notes" rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional notes saved with the quotation" />
            </>
          ) : null}

          {step === 1 ? (
            <>
              <div className="jdh-builder-title"><div><span>Step 2</span><h1>Customer</h1><p>Select the CRM contact this quotation belongs to. Their commercial context determines the scoped document template.</p></div><button type="button" className="secondary-action" onClick={() => { setPrefillLeadName(leadQuery); setAddLeadOpen(true); }}><PersonAddOutlined />Add contact</button></div>
              <div className="jdh-contact-search"><span>Search contacts <b>*</b></span><CrmSearchInput value={leadQuery} onChange={(event) => setLeadQuery(event.target.value)} placeholder="Name, company, email or phone" /></div>
              <div className="jdh-contact-list">
                {visibleLeads.map((lead) => (
                  <button key={lead.id} type="button" className={String(selectedLead?.id) === String(lead.id) ? 'is-selected' : ''} onClick={() => selectLead(lead)}>
                    <span className="jdh-contact-avatar">{leadName(lead).charAt(0).toUpperCase()}</span>
                    <span><strong>{leadName(lead)}</strong><small>{[companyName(lead), lead.email || lead.phone_number].filter(Boolean).join(' · ') || 'No contact details'}</small></span>
                    {String(selectedLead?.id) === String(lead.id) ? <Check /> : null}
                  </button>
                ))}
              </div>
              {selectedLead ? (
                <section className="jdh-selected-contact">
                  <div><small>Name</small><strong>{leadName(selectedLead) || '—'}</strong></div>
                  <div><small>Company</small><strong>{companyName(selectedLead) || '—'}</strong></div>
                  <div><small>Email</small><strong>{selectedLead.email || ''}</strong></div>
                  <div><small>Phone</small><strong>{selectedLead.phone_number || ''}</strong></div>
                  <div><small>GSTIN</small><strong>{selectedLead.gst_number || selectedLead.gstin || ''}</strong></div>
                  <div className="wide"><small>Billing / contact address</small><strong>{contactAddress(selectedLead)}</strong></div>
                </section>
              ) : null}
            </>
          ) : null}

          {quotationMode === 'CATERING' && step === 2 ? (
            <>
              <div className="jdh-builder-title"><div><span>Step 3</span><h1>Event & PAX</h1><p>Event data is retained on the quotation and continues into the catering workflow.</p></div></div>
              <div className="jdh-builder-fields two-col">
                <CrmInput label="Event Name" required value={cateringMeta.event_name || ''} onChange={(event) => setCateringMeta((current) => ({ ...current, event_name: event.target.value }))} />
                <CrmInput label="PAX" required type="number" min="1" value={pax ?? ''} onChange={(event) => setPax(event.target.value === '' ? null : Math.max(1, Number(event.target.value)))} />
                <CrmInput className="wide" label="Event Venue" required value={cateringMeta.event_location || ''} onChange={(event) => setCateringMeta((current) => ({ ...current, event_location: event.target.value }))} />
                <CrmDateInput label="Start Date" required value={cateringMeta.event_start_date || ''} max={cateringMeta.event_end_date || undefined} dateFormat={dateFormat} onChange={(value) => setCateringMeta((current) => ({ ...current, event_start_date: value }))} />
                <CrmTimeInput label="Start Time" required value={cateringMeta.event_start_time || ''} max={cateringMeta.event_start_date && cateringMeta.event_start_date === cateringMeta.event_end_date ? (cateringMeta.event_end_time || undefined) : undefined} onChange={(event) => setCateringMeta((current) => ({ ...current, event_start_time: event.target.value }))} />
                <CrmDateInput label="End Date" required value={cateringMeta.event_end_date || ''} min={cateringMeta.event_start_date || undefined} dateFormat={dateFormat} onChange={(value) => setCateringMeta((current) => ({ ...current, event_end_date: value }))} />
                <CrmTimeInput label="End Time" required value={cateringMeta.event_end_time || ''} min={cateringMeta.event_start_date && cateringMeta.event_start_date === cateringMeta.event_end_date ? (cateringMeta.event_start_time || undefined) : undefined} onChange={(event) => setCateringMeta((current) => ({ ...current, event_end_time: event.target.value }))} />
              </div>
            </>
          ) : null}

          {step === productStepIndex ? (
            <>
              <div className="jdh-builder-title"><div><span>Step {productStepIndex + 1}</span><h1>Products & pricing</h1><p>Select multiple products together, then fine-tune quantity, selling price and discount on the quotation.</p></div><div className="jdh-builder-title-actions"><button type="button" className="secondary-action" onClick={() => setOpenProductDialog(true)}><Add />New product</button><button type="button" className="primary-action" onClick={() => setProductPickerOpen(true)}><Inventory2Outlined />Add products</button></div></div>

              {quotationMode === 'CATERING' ? <QuotationPackageManager packages={packages} setPackages={setPackages} items={items} setItems={setItems} eventPax={pax} currency={currency} gstPricingMode={gstPricingMode} /> : null}

              <div className="jdh-order-lines-scroll">
                <table className="jdh-order-lines">
                  <thead><tr><th aria-label="Reorder" /><th>#</th><th>Item</th><th>Package</th><th>Category</th><th>Qty</th><th>Rate</th><th>Discount</th><th>GST</th><th>Total</th><th /></tr></thead>
                  <tbody>
                    {items.length ? items.map((item, index) => (
                      <tr
                        key={`${item.product?.id || 'item'}-${index}`}
                        className={dragIndex === index ? 'is-dragging' : ''}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          reorderItems(dragIndex, index);
                          setDragIndex(null);
                        }}
                      >
                        <td><button type="button" className="jdh-order-lines__drag" draggable onDragStart={() => setDragIndex(index)} onDragEnd={() => setDragIndex(null)} aria-label={`Reorder ${productName(item.product)}`}><DragIndicator /></button></td>
                        <td>{index + 1}</td>
                        <td><strong>{productName(item.product)}</strong><small>{item.product?.sku || 'No SKU'}</small></td>
                        <td><CrmSelect className="jdh-table-control" label="" value={item.package_key || ''} onChange={(event) => updateItem(index, { package_key: event.target.value || null })}><option value="">Shared / ungrouped</option>{packages.map((pkg) => <option key={pkg.key} value={pkg.key}>{pkg.name || 'Package'}</option>)}</CrmSelect></td>
                        <td>{productCategory(item.product)}</td>
                        <td><CrmInput className="jdh-table-control" type="number" min="1" value={item.quantity} onChange={(event) => updateItem(index, { quantity: event.target.value === '' ? '' : Math.max(1, Number(event.target.value)) })} /></td>
                        <td><div className="jdh-money-input"><span>{currency}</span><CrmInput className="jdh-table-control" type="number" min="0" value={item.selling_price} onChange={(event) => updateItem(index, { selling_price: event.target.value === '' ? '' : Math.max(0, Number(event.target.value)) })} /></div><small>{item.product?.selling_price_unit ? `per ${item.product.selling_price_unit}` : 'per unit'}</small></td>
                        <td><div className="jdh-money-input"><span>{currency}</span><CrmInput className="jdh-table-control" type="number" min="0" value={item.discount} onChange={(event) => updateItem(index, { discount: event.target.value === '' ? '' : Math.max(0, Number(event.target.value)) })} /></div></td>
                        <td>{Number(item.gst_rate || 0)}%</td>
                        <td><strong>{money(lineTotal(item), currency)}</strong></td>
                        <td><IconButton size="small" aria-label="Remove item" onClick={() => removeItem(index)}><DeleteOutline /></IconButton></td>
                      </tr>
                    )) : <tr><td colSpan="11" className="jdh-order-lines__empty"><Inventory2Outlined /><strong>No products added yet</strong><span>Use Add products to select one or more items from the catalog.</span><button type="button" className="primary-action" onClick={() => setProductPickerOpen(true)}>Add products</button></td></tr>}
                  </tbody>
                </table>
              </div>

              {quotationMode === 'CATERING' ? <div className="jdh-pax-check"><span>Package pricing</span><strong>{packages.length ? `${packages.length} guest package${packages.length === 1 ? '' : 's'} configured` : 'No package groups — line items priced as entered'}</strong></div> : null}

              <section className="jdh-order-summary">
                <h2>Quotation summary</h2>
                <div><span>Subtotal</span><strong>{money(totals.discountedSubtotal, currency)}</strong></div>
                <div><span>Item discounts</span><strong>− {money(totals.itemDiscount, currency)}</strong></div>
                <div className="jdh-summary-edit"><label>Overall discount</label><div className="jdh-money-input"><span>{currency}</span><CrmInput className="jdh-summary-control" type="number" min="0" value={overallDiscount} onChange={(event) => setOverallDiscount(Math.max(0, Number(event.target.value || 0)))} /></div></div>
                <div><span>{gstPricingMode === 'INCLUSIVE' ? 'GST included' : 'GST added'}</span><strong>{money(totals.totalTax, currency)}</strong></div>
                <div className="jdh-summary-edit"><label>Rounding +/-</label><div className="jdh-money-input"><span>{currency}</span><CrmInput className="jdh-summary-control" type="number" step="0.01" value={roundingAmount ?? 0} onChange={(event) => { setRoundingManual(true); setRoundingAmount(Number(event.target.value || 0)); }} /></div></div>
                <div className="is-total"><span>Grand total</span><strong>{money(Number(totals.grandTotal || 0) + Number(roundingAmount || 0), currency)}</strong></div>
              </section>
            </>
          ) : null}

          {step === reviewStepIndex ? (
            <>
              <div className="jdh-builder-title"><div><span>Final step</span><h1>Review quotation</h1><p>Confirm the CRM contact, event, products and resolved document template before creating the quotation.</p></div></div>
              <div className="jdh-review-grid">
                <section><h2>Details <button type="button" onClick={() => setStep(0)}>Edit</button></h2><p><b>Template</b>{resolvedProfile?.name || 'Default quotation template'}</p><p><b>Scope</b>{profileScopeLabel(resolvedProfile, catalog)}</p><p><b>Date</b>{formatDate(quotationDate, dateFormat) || ''}</p><p><b>Valid until</b>{formatDate(validUntil, dateFormat) || ''}</p></section>
                <section><h2>Customer <button type="button" onClick={() => setStep(1)}>Edit</button></h2><p><b>Name</b>{leadName(selectedLead)}</p><p><b>Company</b>{companyName(selectedLead)}</p><p><b>Email</b>{selectedLead?.email || ''}</p><p><b>GSTIN</b>{selectedLead?.gst_number || selectedLead?.gstin || ''}</p></section>
                {quotationMode === 'CATERING' ? <section><h2>Event <button type="button" onClick={() => setStep(2)}>Edit</button></h2><p><b>Event</b>{cateringMeta.event_name || ''}</p><p><b>PAX</b>{pax || ''}</p><p><b>Venue</b>{cateringMeta.event_location || ''}</p>{cateringMeta.event_start_date || cateringMeta.event_end_date ? <p><b>Dates</b>{[formatDate(cateringMeta.event_start_date, dateFormat), formatDate(cateringMeta.event_end_date, dateFormat)].filter(Boolean).join(' → ')}</p> : null}</section> : null}
                <section><h2>Products <button type="button" onClick={() => setStep(productStepIndex)}>Edit</button></h2><p><b>Line items</b>{items.length}</p><p><b>Item discount</b>{money(totals.itemDiscount, currency)}</p><p><b>Overall discount</b>{money(overallDiscount, currency)}</p>{quotationMode === 'CATERING' && packages.length ? <p><b>Guest packages</b>{packages.map((pkg) => `${pkg.name} (${pkg.pax} PAX)`).join(' · ')}</p> : null}<p><b>Total</b>{money(Number(totals.grandTotal || 0) + Number(roundingAmount || 0), currency)}</p></section>
                {notes ? <section className="wide"><h2>Notes <button type="button" onClick={() => setStep(0)}>Edit</button></h2><p className="jdh-review-notes">{notes}</p></section> : null}
              </div>
              <div className="jdh-final-action"><div><strong>Ready to create</strong><span>The quotation will be created as Pending using the resolved commercial context and document architecture.</span></div><button type="button" className="primary-action" onClick={handleSubmit} disabled={saving}>{saving ? 'Creating…' : 'Create quotation'}</button></div>
            </>
          ) : null}
        </div>

        <QuotationPreview
          profile={resolvedProfile}
          profileLoading={profileLoading}
          profileError={profileError}
          context={crmCommercialContext}
          selectedLead={selectedLead}
          quotationDate={quotationDate}
          validUntil={validUntil}
          items={pricedItems}
          cateringMeta={cateringMeta}
          pax={pax}
          quotationMode={quotationMode}
          gstPricingMode={gstPricingMode}
          overallDiscount={overallDiscount}
          roundingAmount={roundingAmount}
        />
      </section>

      <footer className="jdh-builder-footer">
        <div><button type="button" onClick={() => setStep((current) => Math.max(0, current - 1))} disabled={!step}><ChevronLeft />Back</button><button type="button" className="exit-link" onClick={() => navigate('/quotations')}>Exit</button></div>
        {step < reviewStepIndex ? <button type="button" className="primary" onClick={next}>Next<ChevronRight /></button> : null}
      </footer>

      <CrmDrawer
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        title="Add products"
        subtitle="Search, filter and select one or more products."
        size="xl"
        className="jdh-product-picker-drawer"
        footer={<><span>{pickedProducts.length} selected</span><div style={{ flex: 1 }} /><CrmButton className="jdh-product-picker-add" variant="primary" startIcon={<Add />} disabled={!pickedProducts.length} onClick={addPickedProducts}>Add selected</CrmButton></>}
      >
        <div className="jdh-product-picker-tools">
          <CrmSearchInput value={productQuery} onChange={(event) => setProductQuery(event.target.value)} placeholder="Search products" />
          {quotationMode === 'CATERING' ? <CrmSelect label="Add to package" value={pickerPackageKey} onChange={(event) => setPickerPackageKey(event.target.value)}><option value="">Shared / ungrouped</option>{packages.map((pkg) => <option key={pkg.key} value={pkg.key}>{pkg.name || 'Package'} · {pkg.pax || 0} PAX</option>)}</CrmSelect> : null}
          <CrmSelect value={productCategoryFilter} onChange={(event) => setProductCategoryFilter(event.target.value)}><option value="all">All categories</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}</CrmSelect>
          <CrmSelect value={productSort} onChange={(event) => setProductSort(event.target.value)}><option value="name-asc">Name A–Z</option><option value="name-desc">Name Z–A</option><option value="price-asc">Lowest price</option><option value="price-desc">Highest price</option></CrmSelect>
        </div>
        <div className="jdh-product-picker-table"><table><thead><tr><th /><th>Product</th><th>Category</th><th>SKU</th><th>Price</th><th>GST</th></tr></thead><tbody>{pickerProducts.map((product) => {
          const key = String(product.id);
          const alreadyAdded = items.some((item) => String(item.product?.id) === key && String(item.package_key || '') === String((quotationMode === 'CATERING' ? pickerPackageKey : '') || ''));
          const selected = pickedProducts.includes(key);
          const toggle = () => { if (!alreadyAdded) setPickedProducts((current) => current.includes(key) ? current.filter((id) => id !== key) : [...current, key]); };
          return <tr key={product.id} className={`${alreadyAdded ? 'is-added' : ''} ${selected ? 'is-selected' : ''}`} onClick={toggle} aria-selected={selected || alreadyAdded}>
            <td onClick={(event) => event.stopPropagation()}><CrmCheckbox disabled={alreadyAdded} checked={selected || alreadyAdded} onChange={toggle} aria-label={`Select ${productName(product)}`} /></td>
            <td><strong>{productName(product)}</strong></td><td>{productCategory(product)}</td><td>{product.sku || ''}</td><td>{money(product.selling_price || product.price, currency)}</td><td>{Number(product.gst_rate || 0)}%</td>
          </tr>;
        })}</tbody></table></div>
      </CrmDrawer>

      <AddProductDialog
        open={openProductDialog}
        onClose={() => { setOpenProductDialog(false); setEditingProduct(null); }}
        onAddProduct={handleAddProduct}
        productToEdit={editingProduct}
      />

      <AddLeadDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        prefillName={prefillLeadName}
        showNotification={showNotification}
        onLeadCreated={async (newLeadId) => {
          const response = await fetchLeads();
          const nextLeads = Array.isArray(response) ? response : response?.leads || response?.data || [];
          setLeads(Array.isArray(nextLeads) ? nextLeads : []);
          const match = (nextLeads || []).find((lead) => String(lead.id) === String(newLeadId));
          if (match) selectLead(match);
        }}
      />

      <NotificationSnackbar open={notif.open} message={notif.message} severity={notif.severity} onClose={() => setNotif((current) => ({ ...current, open: false }))} />
    </main>
  );
}

export default CreateQuotation;
